import {
  ComponentPropsContext,
  EditingComponentPlaceholder,
  handleEditorFastRefresh,
  RenderingType,
  RouteData,
  SitecoreContext,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { SitecorePageProps } from 'lib/page-props';
import { sitecorePagePropsFactory } from 'lib/page-props-factory';
import { GetServerSideProps } from 'next';
import { useEffect } from 'react';
import Layout from 'src/Layout';
import Script from 'next/script';
import { useState } from 'react';
import { useRouter } from 'next/router';

// different componentFactory method will be used based on whether page is being edited
// import Cookies from 'cookies';
import { componentFactory, editingComponentFactory } from 'temp/componentFactory';
import { trackEvent } from 'lib/utils/gtmService';
import { CR_SITE } from 'lib/constant';

const SiteSelectionSuccessPage: React.FC = ({
  notFound,
  componentProps,
  layoutData,
}: SitecorePageProps) => {
  const { sitecoreContext } = useSitecoreContext();
  const siteName = sitecoreContext?.site?.name;
  const router = useRouter();
  const [variant, setVariant] = useState<string>('');

  useEffect(() => {
    // Since Sitecore editors do not support Fast Refresh, need to refresh editor chromes after Fast Refresh finished
    handleEditorFastRefresh();
  }, []);

  useEffect(() => {
    const successPageVariantName =
      ((layoutData?.sitecore?.context?.CDP as any)?.successPageVariantName as string) ||
      'Site Selection Success page';
    if (variant !== successPageVariantName) {
      setVariant(successPageVariantName);
      trackEvent(
        {
          event: `Landed on ${successPageVariantName}`,
        },
        siteName === CR_SITE
      );
    }
  }, [layoutData?.sitecore?.context]);

  if (notFound || !layoutData.sitecore.route) {
    // Shouldn't hit this (as long as 'notFound' is being returned below), but just to be safe
    router.push('/404');
  }

  const isEditing = layoutData.sitecore.context.pageEditing;
  const isComponentRendering =
    layoutData.sitecore.context.renderingType === RenderingType.Component;

  const humanApiCDN =
    process.env.NEXT_PUBLIC_HUMAN_API_CDN ||
    'https://cdn.humanapi.co/humanapi-connect-client@latest.js';

  return (
    <ComponentPropsContext value={componentProps}>
      <SitecoreContext
        componentFactory={isEditing ? editingComponentFactory : componentFactory}
        layoutData={layoutData}
      >
        {/*
          Sitecore Pages supports component rendering to avoid refreshing the entire page during component editing.
          If you are using Experience Editor only, this logic can be removed, Layout can be left.
        */}
        {isComponentRendering ? (
          <EditingComponentPlaceholder rendering={layoutData.sitecore.route as RouteData} />
        ) : (
          <>
            <Script src={humanApiCDN} strategy="beforeInteractive" />
            <Layout layoutData={layoutData} />
          </>
        )}
      </SitecoreContext>
    </ComponentPropsContext>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locales, locale, defaultLocale, params } = context;
  let path = ['siteselectionsuccess'];
  if (context.req.cookies['epr-name']) {
    let eprNameWithLocale = context.req.cookies['epr-name'];
    if (eprNameWithLocale.includes(`/${locale}/`)) {
      eprNameWithLocale = eprNameWithLocale.replace(`/${locale}/`, '');
    } else {
      eprNameWithLocale = eprNameWithLocale.substring(1);
    }
    path = [...eprNameWithLocale.split('/'), ...path];
  }
  const newContext = {
    ...context,
    params: { site: params?.site, path },
    locales,
    locale,
    defaultLocale,
  };

  const props = await sitecorePagePropsFactory.create(newContext);

  if (props.redirect) {
    return {
      redirect: props.redirect,
    };
  }

  return {
    props,
    notFound: props.notFound, // Returns custom 404 page with a status code of 404 when true
  };
};

export default SiteSelectionSuccessPage;
